import { ReactNode } from 'react';
import styled from 'styled-components';

import { H2 } from 'components/Text/H2';
import { useHideDocumentScroll } from 'hooks/useHideDocumentScroll';
import { useIsMounted } from 'hooks/useIsMounted';
import { CloseButton, Dialog, ModalBase } from 'modals/ModalBase';
import { colors, cssBreakpoints } from 'theme/theme';

const StyledDialog = styled(Dialog)<{
  $maxWidth?: 'none' | number;
  $wide?: boolean;
}>`
  background-image: linear-gradient(to left, #1d91e9, ${colors.hoverBlue});
  background-size: 100% 7px;
  background-repeat: no-repeat;
  padding-top: 42px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 35px;
  ${(props) =>
    props.$maxWidth === 'none'
      ? 'max-width: none;'
      : `max-width: ${props.$maxWidth}px;`};

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding-top: 40px;
    padding-left: 40px;
    padding-right: 40px;
    padding-bottom: 40px;
    ${(props) => props.$wide && `min-width: 650px`}
  }
`;

const StyledCloseButton = styled(CloseButton)`
  color: ${colors.mediumContentGrey};
  height: 24px;
  width: 24px;
  &:hover {
    color: ${colors.brandBlue};
    &:disabled {
      &:hover {
        color: ${colors.lightContentGrey};
      }
    }
  }
`;

type Props = {
  children: ReactNode;
  className?: string;
  uncloseable?: boolean;
  customOverlay?: ReactNode;
  image?: ReactNode;
  title?: ReactNode;
  onClose?: () => void;
  'data-qa-id'?: string;
  wide?: boolean;
  maxWidth?: 'none' | number;
};

export function Modal({
  children,
  className,
  uncloseable,
  customOverlay,
  title,
  image,
  onClose,
  wide,
  'data-qa-id': dataQaId,
  maxWidth,
}: Props) {
  const isMounted = useIsMounted();

  useHideDocumentScroll({
    show: isMounted(),
  });

  return (
    <ModalBase
      className={className}
      onClose={onClose}
      customOverlay={customOverlay}
    >
      <StyledDialog data-qa-id={dataQaId} $wide={wide} $maxWidth={maxWidth}>
        {onClose && !uncloseable && (
          <StyledCloseButton
            onClick={onClose}
            data-qa-id="close-modal-button"
            data-modal-focus="false"
          />
        )}

        {image}

        <div>
          {title && (
            <H2 styleAs="h3" pb={2}>
              {title}
            </H2>
          )}

          {children}
        </div>
      </StyledDialog>
    </ModalBase>
  );
}
